import { useState, type CSSProperties, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CustomFieldInputs } from '../../components/CustomFields';
import { Icon } from '../../components/Icon';
import { Logo } from '../../components/Logo';
import { api } from '../../lib/api';
import { date } from '../../lib/format';

interface PublicField {
  id: string;
  key: string;
  label: string;
  type: string;
  options: string[];
  required: boolean;
  hint: string | null;
}

interface PublicJobPage {
  tenantName: string;
  logoUrl: string | null;
  brandColor: string | null;
  title: string;
  department: string | null;
  employmentLabel: string;
  workModeLabel: string | null;
  location: string | null;
  description: string | null;
  duties: string[];
  techRequirements: string[];
  softRequirements: string[];
  benefits: string[];
  languages: string[];
  experienceMin: number | null;
  educationLabel: string | null;
  /** فارغٌ حين لا تُعلن الشركةُ النطاق — ولا يُرسَل الرقمُ أصلًا. */
  salary: { min: number | null; max: number | null } | null;
  closesOn: string | null;
  intro: string | null;
  fields: PublicField[];
}

/**
 * قائمةٌ في الصفحة العلنيّة — لا تُرسم فارغةً.
 *
 * عنوانٌ بلا بنودٍ تحته يقول للقارئ إنّ الشركةَ نسيت شيئًا، وهو أسوأُ من
 * ألّا يُعرض القسمُ أصلًا.
 */
function Bullets({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <section className="mt-4">
      <h3 className="card-title">{title}</h3>
      <ul className="rc-qlist">
        {items.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
    </section>
  );
}

/** نطاقُ الراتب مقروءًا — وحدٌّ واحدٌ يُقرأ «من» أو «حتى» لا نصفَ نطاق. */
function salaryChip(s: { min: number | null; max: number | null }): string {
  const fmt = (n: number) => n.toLocaleString('ar-EG');
  if (s.min !== null && s.max !== null) return `${fmt(s.min)} — ${fmt(s.max)}`;
  if (s.min !== null) return `من ${fmt(s.min)}`;
  if (s.max !== null) return `حتى ${fmt(s.max)}`;
  return '';
}

/**
 * صفحة التقديم على وظيفة.
 *
 * صفحةُ الشركة أمام من يطرق بابها لا صفحتُنا: بلا شريطٍ علويّ ولا قائمة،
 * وبشعارها ولونها. ومن يفتحها ليس مستخدمًا في النظام — فلا حساب ولا كلمة
 * سرّ، وطلبُ ذلك قبل التقديم يخسر أكثر من نصف من يفتحون الرابط.
 */
export function PublicJob() {
  const { tenant = '', slug = '' } = useParams();
  const [params] = useSearchParams();
  const base = `/public/jobs/${encodeURIComponent(tenant)}/${encodeURIComponent(slug)}`;

  /*
   * القناةُ من عنوان الرابط لا من سؤالٍ في الاستمارة.
   *
   * «من أين عرفتَ عنّا؟» يُجاب عنه بالتخمين أو يُترك، وإنفاقُ ميزانيةٍ على
   * أرقامٍ مصدرُها تخمينُ المتقدّمين إنفاقٌ على لا شيء. والرابطُ الذي نُشر
   * في لينكدإن يحمل ذلك في عنوانه فيُسجَّل بلا سؤال.
   *
   * ولا يُعرض للمتقدّم ولا يُشرح له: هو قياسٌ داخليٌّ لا يخصّه.
   */
  const channel = params.get('c');

  const page = useQuery({
    queryKey: ['apply', 'page', tenant, slug],
    queryFn: () => api<PublicJobPage>(base, { skipAuth: true }),
    retry: false,
  });

  if (page.isPending) {
    return (
      <Frame>
        <div className="skeleton" style={{ height: 220 }} />
      </Frame>
    );
  }

  if (page.isError) {
    return (
      <Frame>
        <div className="book-done">
          <h1 className="book-title">الرابط لم يعد يعمل</h1>
          <p className="mute">
            {page.error instanceof Error
              ? page.error.message
              : 'هذه الوظيفة غير موجودة أو لم تعد متاحة.'}
          </p>
        </div>
      </Frame>
    );
  }

  return (
    <ApplyScreen
      info={page.data}
      channel={channel}
      submit={(body) =>
        api<{ ok: true; code: string | null }>(base, {
          method: 'POST',
          skipAuth: true,
          body,
        })
      }
    />
  );
}

function ApplyScreen({
  info,
  submit,
  channel,
}: {
  info: PublicJobPage;
  submit: (body: Record<string, unknown>) => Promise<{ code: string | null }>;
  /** القناةُ من عنوان الرابط — تُمرَّر كما جاءت ويردّ الخادمُ ما لا يعرفه إلى الفراغ. */
  channel: string | null;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [headline, setHeadline] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [done, setDone] = useState<string | null>(null);

  /* لون الشركة يصبغ الأزرار — الصفحة صفحتها لا صفحتنا. */
  const brand = info.brandColor
    ? ({
        ['--book-brand' as string]: info.brandColor,
        ['--book-brand-ink' as string]: readableOn(info.brandColor),
      } as CSSProperties)
    : undefined;

  const missing = info.fields.filter(
    (f) => f.required && !(answers[f.key] ?? '').trim(),
  );
  const valid =
    name.trim().length >= 2 && phone.trim().length >= 6 && missing.length === 0;

  const send = useMutation({
    mutationFn: () =>
      submit({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || null,
        headline: headline.trim() || null,
        answers: toAnswers(info.fields, answers),
        channel,
      }),
    onSuccess: (r) => setDone(r.code ?? ''),
  });

  if (done !== null) {
    return (
      <Frame brandStyle={brand}>
        <Identity info={info} />
        <div className="book-done">
          <span className="book-done-mark" aria-hidden="true">
            <Icon name="check" size={22} />
          </span>
          <h2 className="book-title">وصل طلبك</h2>
          <p className="mute">
            سنراجع ملفّك ونتواصل معك على الرقم الذي كتبته. ولا حاجة إلى
            إرساله مرّةً أخرى.
          </p>
          {done && (
            <p className="tiny mute">
              رقم ملفّك: <strong dir="ltr">{done}</strong>
            </p>
          )}
        </div>
      </Frame>
    );
  }

  return (
    <Frame brandStyle={brand} wide>
      <Identity info={info} />

      <div className="book-meta">
        <span className="rc-chip">{info.employmentLabel}</span>
        {info.workModeLabel && (
          <span className="rc-chip">{info.workModeLabel}</span>
        )}
        {info.department && <span className="rc-chip">{info.department}</span>}
        {info.location && <span className="rc-chip">{info.location}</span>}
        {/*
          الراتبُ في الأعلى لا في آخر الصفحة: هو أوّلُ ما يبحث عنه من يقرأ
          إعلانًا، ومن يخفيه إلى الأسفل يجعله يمرّ على عشرة أسطرٍ بحثًا عنه.
          ولا يظهر إلّا إن أذنت الشركةُ بإعلانه — وحينها وحدَها يصل الرقم.
        */}
        {info.salary && (
          <span className="rc-chip">{salaryChip(info.salary)}</span>
        )}
        {info.experienceMin !== null && (
          <span className="rc-chip">خبرة {info.experienceMin} سنة فأكثر</span>
        )}
        {info.educationLabel && (
          <span className="rc-chip">{info.educationLabel}</span>
        )}
        {info.closesOn && (
          <span className="rc-chip">التقديم حتى {date(info.closesOn)}</span>
        )}
      </div>

      {info.description && (
        <section className="mt-4">
          <h3 className="card-title">عن الوظيفة</h3>
          <p className="rc-text">{info.description}</p>
        </section>
      )}

      <Bullets title="المهامّ" items={info.duties} />
      <Bullets title="المطلوب" items={info.techRequirements} />
      <Bullets title="نبحث عمّن" items={info.softRequirements} />
      <Bullets title="اللغات" items={info.languages} />
      <Bullets title="ما نقدّمه" items={info.benefits} />

      <section className="mt-4">
        <h3 className="card-title">قدّم الآن</h3>
        {info.intro && <p className="mute">{info.intro}</p>}

        <div className="form-grid mt-2">
          <div className="field">
            <label className="label req" htmlFor="pj-name">
              الاسم كاملًا
            </label>
            <input
              id="pj-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="label req" htmlFor="pj-phone">
              رقم الهاتف
            </label>
            <input
              id="pj-phone"
              className="input num"
              dir="ltr"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <span className="tiny mute">عليه نتواصل معك.</span>
          </div>

          <div className="field">
            <label className="label" htmlFor="pj-email">
              البريد الإلكتروني
            </label>
            <input
              id="pj-email"
              className="input"
              dir="ltr"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="pj-headline">
              مسمّاك الوظيفي الحالي
            </label>
            <input
              id="pj-headline"
              className="input"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
            />
          </div>

          <CustomFieldInputs
            defs={info.fields.map((f) => ({
              key: f.key,
              label: f.label,
              type: f.type as never,
              options: f.options,
              required: f.required,
              hint: f.hint,
            }))}
            values={answers}
            onChange={(k, v) => setAnswers((p) => ({ ...p, [k]: v }))}
            idPrefix="pj"
          />
        </div>

        {send.isError && (
          <p className="tiny" style={{ color: 'var(--danger)' }}>
            {send.error instanceof Error
              ? send.error.message
              : 'تعذّر الإرسال. حاول بعد قليل.'}
          </p>
        )}

        <button
          className="btn btn-primary btn-lg mt-4"
          onClick={() => send.mutate()}
          disabled={!valid || send.isPending}
        >
          {send.isPending ? 'جارٍ الإرسال…' : 'أرسل طلبي'}
        </button>

        {!valid && (
          <p className="tiny mute mt-2">
            {name.trim().length < 2 || phone.trim().length < 6
              ? 'الاسم ورقم الهاتف مطلوبان.'
              : `بقي: ${missing.map((f) => f.label).join('، ')}.`}
          </p>
        )}
      </section>
    </Frame>
  );
}

/** هوية الشركة: الشعار والاسم — ما يطمئن الغريب أنه في المكان الصحيح. */
function Identity({ info }: { info: PublicJobPage }) {
  return (
    <header className="book-brand">
      {info.logoUrl ? (
        <img className="book-logo" src={info.logoUrl} alt={info.tenantName} />
      ) : (
        <span className="book-logo book-logo-fallback">
          {info.tenantName.slice(0, 2)}
        </span>
      )}
      <div>
        <div className="book-tenant">{info.tenantName}</div>
        <h1 className="book-title">{info.title}</h1>
      </div>
    </header>
  );
}

/** إطار الصفحة — بلا شريط علوي ولا قائمة: الزائر ليس مستخدمًا في النظام. */
function Frame({
  children,
  brandStyle,
  wide,
}: {
  children: ReactNode;
  brandStyle?: CSSProperties;
  wide?: boolean;
}) {
  return (
    <div className="book-page" style={brandStyle}>
      {/*
        بطاقةٌ أعرضُ لا `book-card-wide`: تلك مصنوعةٌ لهيكل الحجز ذي
        العمودين فتُلغي الحشو وتقصّ ما زاد — فيلتصق نصُّ الوظيفة بالحافّة
        ويُقصّ آخرُ حقلٍ في الاستمارة على شاشة الهاتف.
      */}
      <div className={`book-card${wide ? ' rc-apply-card' : ''}`}>
        {children}
      </div>
      <p className="book-foot tiny mute">
        <Logo size={16} /> GoyOne
      </p>
    </div>
  );
}

/**
 * لونُ الخطّ فوق لون الشركة.
 *
 * يُحسب من السطوع النسبيّ لا يُفترض أبيض: شركةٌ لونها أصفر تجعل الزرَّ
 * أبيضَ على أصفر — نصٌّ لا يُقرأ.
 */
function readableOn(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return L > 0.45 ? '#12214a' : '#ffffff';
}

function toAnswers(
  fields: { key: string; type: string }[],
  values: Record<string, string>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const raw = (values[f.key] ?? '').trim();
    if (raw === '') continue;
    if (f.type === 'NUMBER') out[f.key] = Number(raw);
    else if (f.type === 'BOOLEAN') out[f.key] = raw === 'true';
    else out[f.key] = raw;
  }
  return out;
}
